import { useState, useCallback, useRef, memo, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { DragDropProvider } from '@dnd-kit/react';
import { useSortable } from '@dnd-kit/react/sortable';
import type { DragDropEventHandlers } from '@dnd-kit/react';
import { CollisionPriority } from '@dnd-kit/abstract';
import { Feedback, PointerSensor, KeyboardSensor } from '@dnd-kit/dom';
import { move } from '@dnd-kit/helpers';
import { X } from 'lucide-react';
import { skillBucketApi } from '@/lib/api';
import type { SkillBucketListResponse } from '@/lib/api';
import type { SkillRepository, SkillGroup } from '@/lib/skillRepository';
import { getSkillGroups } from '@/lib/skillRepository';
import { Button } from '@/components/ui/button';

const sensors = [
  PointerSensor.configure({
    activatorElements(source) {
      return [source.element, source.handle];
    },
  }),
  KeyboardSensor,
];

// ── Sortable Skill (Item) ──────────────────────────────────────────────────────

interface SortableSkillProps {
  id: string;
  bucketId: string;
  index: number;
  skill: string;
}

const SortableSkill = memo(function SortableSkill({
  id,
  bucketId,
  index,
  skill,
}: SortableSkillProps) {
  const { ref, handleRef, isDragging } = useSortable({
    id,
    type: 'skill',
    group: bucketId,
    accept: 'skill',
    index,
    data: { skill, bucketId },
    plugins: [Feedback.configure({ feedback: 'clone' })],
  });

  return (
    <div
      ref={ref as any}
      className={`inline-block px-2 py-0.5 text-xs bg-blue-100 text-blue-900 rounded-full cursor-grab active:cursor-grabbing ${
        isDragging ? 'opacity-50' : ''
      }`}
    >
      <span ref={handleRef as any}>
        {skill}
      </span>
    </div>
  );
});

// ── Sortable Bucket (Container) ────────────────────────────────────────────────

interface SortableBucketProps {
  id: string;
  index: number;
  label: string;
  skills: string[];
  isExclusion?: boolean;
  onRename?: (newName: string) => void;
  onDelete?: () => void;
}

const SortableBucket = memo(function SortableBucket({
  id,
  index,
  label,
  skills,
  isExclusion,
  onRename,
  onDelete,
}: SortableBucketProps) {
  const [isRenaming, setIsRenaming] = useState(false);
  const [editValue, setEditValue] = useState(label);
  const { ref, handleRef, isDragging } = useSortable({
    id,
    type: 'bucket',
    accept: ['bucket', 'skill'],
    collisionPriority: CollisionPriority.Low,
    index,
  });

  const handleRenameSubmit = () => {
    if (editValue.trim() && editValue.trim() !== label) {
      onRename?.(editValue.trim());
    }
    setIsRenaming(false);
    setEditValue(label);
  };

  const bgClass = isExclusion ? 'bg-gray-50' : '';
  const labelColorClass = isExclusion ? 'text-gray-600' : 'text-gray-900';

  return (
    <div
      ref={ref as any}
      className={`border-b py-2 px-3 transition-colors ${bgClass} ${
        isDragging ? 'bg-blue-50' : 'hover:bg-gray-50'
      }`}
    >
      <div className="flex items-center justify-between mb-2">
        {isRenaming ? (
          <input
            type="text"
            autoFocus
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={handleRenameSubmit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleRenameSubmit();
              if (e.key === 'Escape') {
                setIsRenaming(false);
                setEditValue(label);
              }
            }}
            className="flex-1 px-2 py-1 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        ) : (
          <>
            <p
              ref={handleRef as any}
              onClick={() => {
                if (id !== 'other') setIsRenaming(true);
              }}
              className={`text-sm font-medium ${labelColorClass} mb-0 cursor-grab active:cursor-grabbing ${
                id !== 'other' ? 'hover:underline' : ''
              }`}
            >
              {label}
            </p>
            {id !== 'other' && (
              <button
                onClick={() => onDelete?.()}
                className="ml-2 text-gray-400 hover:text-red-600 transition-colors"
                title="Delete bucket"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        {skills.map((skill, skillIndex) => (
          <SortableSkill
            key={skill}
            id={skill}
            bucketId={id}
            index={skillIndex}
            skill={skill}
          />
        ))}
      </div>
    </div>
  );
});

// ── Main Section ───────────────────────────────────────────────────────────────

interface SkillBucketsSectionProps {
  repository: SkillRepository;
  unclassifiedLabel?: string;
  unclassifiedVariant?: 'default' | 'exclusion';
  onSkillGroupsChange?: (groups: SkillGroup[]) => void;
}

export function SkillBucketsSection({
  repository,
  unclassifiedLabel = 'Unclassified',
  unclassifiedVariant = 'default',
  onSkillGroupsChange,
}: SkillBucketsSectionProps) {
  const queryClient = useQueryClient();
  const { data: apiData, isLoading } = useQuery({
    queryKey: ['skillBuckets'],
    queryFn: skillBucketApi.list,
  });

  // Transform API data to flat structure
  const [items, setItems] = useState<Record<string, string[]>>({});
  const [localBuckets, setLocalBuckets] = useState<Array<{ id: string; name: string }>>([]);
  const itemsRef = useRef<Record<string, string[]>>({});
  const snapshot = useRef<Record<string, string[]> | null>(null);

  // Sync items and buckets from apiData
  useEffect(() => {
    if (apiData) {
      const newItems: Record<string, string[]> = {};
      for (const bucket of apiData.buckets) {
        newItems[bucket.id] = bucket.skills;
      }
      newItems.other = apiData.unclassified;
      setItems(newItems);
      setLocalBuckets(
        apiData.buckets.map((b) => ({ id: b.id, name: b.name }))
      );
    }
  }, [apiData]);

  // Keep itemsRef in sync for use in handleDragEnd
  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  // Fire onSkillGroupsChange when items or buckets change
  useEffect(() => {
    if (onSkillGroupsChange) {
      const groups = getSkillGroups(items, localBuckets);
      onSkillGroupsChange(groups);
    }
  }, [items, localBuckets, onSkillGroupsChange]);

  const handleDragStart = useCallback<DragDropEventHandlers['onDragStart']>(() => {
    snapshot.current = structuredClone(items);
  }, [items]);

  const handleDragOver = useCallback<DragDropEventHandlers['onDragOver']>((event: any) => {
    const { source } = event.operation;

    if (source && source.type === 'bucket') {
      return;
    }

    setItems((items) => move(items, event));
  }, []);

  const handleDragEnd = useCallback<DragDropEventHandlers['onDragEnd']>((event: any) => {
    if (event.canceled) {
      setItems(snapshot.current || {});
      return;
    }

    const prev = snapshot.current || {};
    const curr = itemsRef.current;

    // Find skills that moved to a different bucket
    const movedSkills = new Set<string>();
    for (const [bucketId, skills] of Object.entries(curr)) {
      for (const skill of skills) {
        const prevBucket = Object.entries(prev).find(([, s]) => s.includes(skill))?.[0];
        if (prevBucket && prevBucket !== bucketId) {
          movedSkills.add(skill);
          repository.onSkillMoved(skill, prevBucket, bucketId);
        }
      }
    }

    // Find buckets with reordered skills (within same bucket, no moves into/out)
    for (const [bucketId, skills] of Object.entries(curr)) {
      const prevSkills = prev[bucketId] ?? [];
      if (
        bucketId !== 'other' &&
        skills.length === prevSkills.length &&
        JSON.stringify(skills) !== JSON.stringify(prevSkills) &&
        !skills.some((s) => movedSkills.has(s))
      ) {
        repository.onSkillsReordered(bucketId, skills);
      }
    }
  }, [repository]);

  const handleCreateBucket = async () => {
    const name = prompt('Bucket name:');
    if (!name || !name.trim()) return;

    const id = await repository.createBucket(name.trim());
    setLocalBuckets((prev) => [...prev, { id, name: name.trim() }]);
    setItems((prev) => ({ ...prev, [id]: [] }));
  };

  const handleRenameBucket = async (bucketId: string, newName: string) => {
    await repository.renameBucket(bucketId, newName);
    setLocalBuckets((prev) =>
      prev.map((b) => (b.id === bucketId ? { ...b, name: newName } : b))
    );
  };

  const handleDeleteBucket = async (bucketId: string) => {
    if (!confirm(`Delete bucket?`)) return;

    await repository.deleteBucket(bucketId);
    setLocalBuckets((prev) => prev.filter((b) => b.id !== bucketId));
    setItems((prev) => {
      const updated = { ...prev };
      delete updated[bucketId];
      return updated;
    });
  };

  const handleReorderBuckets = useCallback(
    (orderedIds: string[]) => {
      void repository.reorderBuckets(orderedIds);
    },
    [repository]
  );

  if (isLoading) {
    return <div className="text-sm text-gray-500">Loading skills…</div>;
  }

  if (!apiData) {
    return null;
  }

  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-gray-900">Skill Organization</h2>
        <Button size="sm" variant="outline" onClick={handleCreateBucket}>
          + Add Bucket
        </Button>
      </div>

      <DragDropProvider
        sensors={sensors}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        <div className="border rounded-lg">
          {/* Unclassified/"Don't Include" bucket */}
          <SortableBucket
            id="other"
            index={0}
            label={unclassifiedLabel}
            skills={items.other ?? []}
            isExclusion={unclassifiedVariant === 'exclusion'}
          />

          {/* Named buckets */}
          {localBuckets.map((bucket, bucketIndex) => (
            <SortableBucket
              key={bucket.id}
              id={bucket.id}
              index={bucketIndex + 1}
              label={bucket.name}
              skills={items[bucket.id] ?? []}
              onRename={(newName) => handleRenameBucket(bucket.id, newName)}
              onDelete={() => handleDeleteBucket(bucket.id)}
            />
          ))}
        </div>
      </DragDropProvider>
    </section>
  );
}
